import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { merge, some } from 'lodash';
import { CommonResponse } from 'src/model/common';
import {
  CompanyAddresses,
  CompanyContacts,
  CompanyContactsByAI,
  CompanyDetail,
  CompanyIndustry,
  CompanyMarketPlaces,
  CompanyNames,
  CompanyProfileQuickView,
  CompanyProfiles,
  CompanyReviewDetail,
  CompanyReviews,
  CompanySocialMedia,
  CompanySocialMediaAI,
  CompanySocialReviews,
  CompanyType,
  CompanyWebDetail,
  CompareCompanies,
  Jurisdictions,
  NegativeMediaList,
  RelatedCompanies,
  RelatedCorporations,
  RelatedQuestions,
  SanctionCompanies
} from 'src/model/company';
import { CompanyService, CompanyTradeDataService } from 'src/services/api';
import { useQueryTradeDataCompanyDetail } from 'src/services/queries/useQueryCompanyTradeData';
import { TRADE_DATA_CACHE_TIME, TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME } from 'src/utils/constants/config';
import { standardizeData } from 'src/utils/helpers/dataMappers/dataMappers';

const QUERY_KEY_SEARCH_COMPANY_JURISDICTION = 'QUERY_KEY_SEARCH_COMPANY_JURISDICTION';
const QUERY_KEY_SEARCH_INDUSTRY = 'QUERY_KEY_SEARCH_INDUSTRY';
const QUERY_KEY_SEARCH_COMPANY_TYPES = 'QUERY_KEY_SEARCH_COMPANY_TYPES';
const QUERY_KEY_SEARCH_LIST_COMPANY = 'QUERY_KEY_SEARCH_LIST_COMPANY';
const QUERY_KEY_SEARCH_LIST_COMPANY_DETAIL = 'QUERY_KEY_SEARCH_LIST_COMPANY_DETAIL';
const QUERY_KEY_SEARCH_COMPANY_NAMES = 'QUERY_KEY_SEARCH_COMPANY_NAMES';
export const QUERY_KEY_COMPANY_DETAIL = 'QUERY_KEY_COMPANY_DETAIL';
const QUERY_KEY_COMPANY_WEB_DETAIL = 'QUERY_KEY_COMPANY_WEB_DETAIL';
const QUERY_KEY_COMPANY_SOCIAL_REVIEW = 'QUERY_KEY_COMPANY_SOCIAL_REVIEW';
export const QUERY_KEY_COMPANY_REVIEW_DETAIL = 'QUERY_KEY_COMPANY_REVIEW_DETAIL';
export const QUERY_KEY_COMPANY_REVIEW = 'QUERY_KEY_COMPANY_REVIEW';
const QUERY_KEY_COMPARE_COMPANIES = 'QUERY_KEY_COMPARE_COMPANIES';
const QUERY_KEY_COMPANY_MARKET_PLACE = 'QUERY_KEY_COMPANY_MARKET_PLACE';
const QUERY_KEY_COMPANY_CONTACTS = 'QUERY_KEY_COMPANY_CONTACTS';
const QUERY_KEY_COMPANY_CONTACTS_AI = 'QUERY_KEY_COMPANY_CONTACTS_AI';
const QUERY_KEY_COMPANY_ADDRESS = 'QUERY_KEY_COMPANY_ADDRESS';
const QUERY_KEY_COMPANY_SOCIAL_MEDIA = 'QUERY_KEY_COMPANY_SOCIAL_MEDIA';
const QUERY_KEY_COMPANY_SOCIAL_MEDIA_AI = 'QUERY_KEY_COMPANY_SOCIAL_MEDIA_AI';
const QUERY_KEY_COMPANY_NEGATIVE_MEDIA_LIST = 'QUERY_KEY_COMPANY_NEGATIVE_MEDIA_LIST';
const QUERY_KEY_COMPANY_EXACT_NEGATIVE_MEDIA_LIST = 'QUERY_KEY_COMPANY_EXACT_NEGATIVE_MEDIA_LIST';
const QUERY_KEY_RELATED_COMPANIES = 'QUERY_KEY_RELATED_COMPANIES';
const QUERY_KEY_RELATED_CORPORATIONS = 'QUERY_KEY_RELATED_CORPORATIONS';
const QUERY_KEY_ALERT_COMPANIES = 'QUERY_KEY_ALERT_COMPANIES';
const QUERY_KEY_RELATED_QUESTIONS = 'QUERY_KEY_RELATED_QUESTIONS';
const QUERY_KEY_CHECK_COMPANY_TAB_IS_EXIST = 'QUERY_KEY_CHECK_COMPANY_TAB_IS_EXIST';
const QUERY_KEY_CHECK_COMPANY_TRADE_DATA_TAB_IS_EXIST = 'QUERY_KEY_CHECK_COMPANY_TRADE_DATA_TAB_IS_EXIST';

export const useQueryJurisdictions = (countryCode?: string[]) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_COMPANY_JURISDICTION, countryCode],
    queryFn: () => CompanyService.getJurisdictions(countryCode),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new Jurisdictions(),
  });
  return {
    data,
    isFetching,
  };
};

export const useQueryIndustry = (countryCode: string[]) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_INDUSTRY, countryCode],
    queryFn: () => CompanyService.getCompanyIndustry(countryCode),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyIndustry(),
  });
  return {
    data,
    isFetching,
  };
};

export const useQueryCompanyTypes = (jurisdiction: string[]) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_COMPANY_TYPES, jurisdiction],
    queryFn: () => CompanyService.getCompanyTypes(jurisdiction),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyType(),
    enabled: jurisdiction && jurisdiction.length > 0,
  });
  return {
    data,
    isFetching,
  };
};

export const useQueryCompanyNames = (
  params: Record<string, string | number | boolean | Array<ExpectedAny>>,
  jurisdiction?: string[],
) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_COMPANY_NAMES, params, jurisdiction],
    queryFn: () => CompanyService.getCompanyNames(params, jurisdiction || []),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyNames(),
    enabled: !!params.company_name,
  });
  return {
    data,
    isFetching,
  };
};

export const useQueryListCompany = (query: string) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_LIST_COMPANY, query],
    queryFn: () => CompanyService.getListCompany(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyProfiles(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryQuickViewCompanyProfile = (id: string) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_LIST_COMPANY_DETAIL, id],
    queryFn: () => CompanyService.getQuickViewCompanyProfile(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyProfileQuickView(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(id),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryCompanyDetail = (id: string, enabled: boolean) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_DETAIL, id],
    queryFn: () => CompanyService.getCompanyDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyDetail(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(enabled),
  });
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryCompanyWebDetail = (id: string) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_WEB_DETAIL, id],
    queryFn: () => CompanyService.getCompanyWebDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new CompanyWebDetail(),
  });
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryCompanySocialReview = (props: { id?: string }) => {
  return useQuery({
    queryKey: [QUERY_KEY_COMPANY_SOCIAL_REVIEW, props.id],
    queryFn: () => CompanyService.getCompanyReviewFromSocial(props.id ?? ''),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanySocialReviews(),
    enabled: Boolean(props.id),
  });
};

export const useQueryCompanyReviewDetail = (props: { id?: string }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_REVIEW_DETAIL, props.id],
    queryFn: () => CompanyService.getCompanyReviewDetail(props.id ?? ''),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyReviewDetail(),
    enabled: Boolean(props.id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};
export const useQueryCompanyReviews = (props: { id?: string; params: Record<string, string | number> }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_REVIEW, props.id, props.params],
    queryFn: () => CompanyService.getCompanyReviews(props.id ?? '', props.params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyReviews(),
    enabled: Boolean(props.id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};

export const useQueryCompareCompanies = (companyIds: string[]) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_COMPARE_COMPANIES, companyIds],
    queryFn: () => CompanyService.getCompareCompanies(companyIds),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompareCompanies(),
  });
  return {
    data,
    isFetching,
  };
};

export const useQueryCompanyMarketPlace = (props: { id: string; params: Record<string, string | number> }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_MARKET_PLACE, props.id, props.params],
    queryFn: () => CompanyService.getCompanyMarketplace(props.id, props.params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyMarketPlaces(),
    enabled: Boolean(props.id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};

export const useQueryCompanyContacts = ({ id, refer_from }: { id: string; refer_from: string }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_CONTACTS, id],
    queryFn: () => CompanyService.getCompanyContacts({ id, refer_from }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyContacts(),
    enabled: Boolean(id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};

export const useQueryCompanyContactsAI = ({ id }: { id: string }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_CONTACTS_AI, id],
    queryFn: () => CompanyService.getCompanyContactsByAI({ id }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyContactsByAI(),
    enabled: Boolean(id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};

export const useQueryCompanyAddress = ({ id }: { id: string }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_ADDRESS, id],
    queryFn: () => CompanyService.getCompanyAddress(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAddresses(),
  });
  return {
    isFetching,
    data,
    refetch,
  };
};

export const useQueryCompanySocialMedia = ({ id }: { id: string }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_SOCIAL_MEDIA, id],
    queryFn: () => CompanyService.getCompanySocialMedia(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanySocialMedia(),
    enabled: Boolean(id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};

export const useQueryCompanySocialMediaAI = ({ id }: { id: string }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_SOCIAL_MEDIA_AI, id],
    queryFn: () => CompanyService.getCompanySocialMediaByAI(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanySocialMediaAI(),
    enabled: Boolean(id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};

export const useQueryCompanyNegativeMedia = ({ id, params }: { id?: string; params: Record<string, number> }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_NEGATIVE_MEDIA_LIST, id, params],
    queryFn: () => CompanyService.getListNegativeMedia(params, id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new NegativeMediaList(),
    enabled: Boolean(id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};
export const useQueryCompanyExactNegativeMedia = ({ id, params }: { id?: string; params: Record<string, number> }) => {
  const { isFetching, refetch, data } = useQuery({
    queryKey: [QUERY_KEY_COMPANY_EXACT_NEGATIVE_MEDIA_LIST, id, params],
    queryFn: () => CompanyService.getListExactNegativeMedia(params, id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new NegativeMediaList(),
    placeholderData: keepPreviousData,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: Boolean(id),
  });
  return {
    isFetching,
    refetch,
    data,
  };
};

export const useQueryListRelatedCorporations = (id: string, query: Record<string, string | number>) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_RELATED_CORPORATIONS, id, query],
    queryFn: () => CompanyService.getListRelatedCorporations(id, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new RelatedCorporations(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryListSanctionsCompanies = ({ id, params }: { id: string; params: Record<string, number> }) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_ALERT_COMPANIES, id, params],
    queryFn: () => CompanyService.getSanctionsCompanies(id, params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new SanctionCompanies(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryRelatedQuestion = ({
  company_id,
  query,
}: {
  company_id?: string;
  query: Record<string, number | string>;
}) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_RELATED_QUESTIONS],
    queryFn: () => CompanyService.getRelatedQuestions(company_id, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new RelatedQuestions(),
    placeholderData: keepPreviousData,
    enabled: Boolean(company_id),
  });
  return { data, isFetching };
};

export const useQueryCheckCompanyTabIsExist = ({
  company_id = '',
  params = {},
}: {
  company_id?: string;
  params: Record<string, ExpectedAny>;
}) => {
  const checkCompanyTradeDataQuery = useQuery({
    queryKey: [QUERY_KEY_CHECK_COMPANY_TRADE_DATA_TAB_IS_EXIST, company_id, params],
    queryFn: () => CompanyTradeDataService.checkCompanyTradeDateTabIsExist(company_id, params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponse(),
    placeholderData: keepPreviousData,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: Boolean(company_id),
  });

  const checkCompanyQuery = useQuery({
    queryKey: [QUERY_KEY_CHECK_COMPANY_TAB_IS_EXIST, company_id],
    queryFn: () => CompanyService.checkCompanyTabIsExist(company_id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponse(),
    placeholderData: keepPreviousData,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: Boolean(company_id),
  });

  const data = merge(checkCompanyTradeDataQuery.data, checkCompanyQuery.data);

  const isFetching = checkCompanyTradeDataQuery.isFetching || checkCompanyQuery.isFetching;

  return {
    data,
    isFetching,
  };
};

export const useQueryListRelatedCompanies = (id: string, query: Record<string, string | number>) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_RELATED_COMPANIES, id, query],
    queryFn: () => CompanyService.getListRelatedCompanies(id, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new RelatedCompanies(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const usePreloadCompanyDetail = ({ id = '' }: { id?: string }) => {
  const overview = useQuery({
    queryKey: [QUERY_KEY_COMPANY_DETAIL, id],
    queryFn: () => CompanyService.getCompanyDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyDetail(),
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    placeholderData: (previousData) => previousData,
    enabled: Boolean(id),
  });

  const overviewFallback = useQueryTradeDataCompanyDetail(id);

  const address = useQuery({
    queryKey: [QUERY_KEY_COMPANY_ADDRESS, id],
    queryFn: () => CompanyService.getCompanyAddress(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new CompanyAddresses(),
  });
  const socialMedia = useQuery({
    queryKey: [QUERY_KEY_COMPANY_SOCIAL_MEDIA_AI, id],
    queryFn: () => CompanyService.getCompanySocialMediaByAI(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new CompanySocialMediaAI(),
    enabled: Boolean(id),
  });

  const review = useQuery({
    queryKey: [QUERY_KEY_COMPANY_REVIEW, id, { limit: 10000, offset: 0 }],
    queryFn: () => CompanyService.getCompanyReviews(id ?? '', { limit: 1000, offset: 0 }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new CompanyReviews(),
    enabled: Boolean(id),
  });

  const contact = useQuery({
    queryKey: [QUERY_KEY_COMPANY_CONTACTS_AI, id],
    queryFn: () => CompanyService.getCompanyContactsByAI({ id }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new CompanyContactsByAI(),
    enabled: Boolean(id),
  });

  const socialReview = useQuery({
    queryKey: [QUERY_KEY_COMPANY_SOCIAL_REVIEW, id],
    queryFn: () => CompanyService.getCompanyReviewFromSocial(id ?? ''),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new CompanySocialReviews(),
    placeholderData: keepPreviousData,
    enabled: Boolean(id),
  });
  const reviewRelatedQuestion = useQuery({
    queryKey: [QUERY_KEY_RELATED_QUESTIONS, id],
    queryFn: () =>
      CompanyService.getRelatedQuestions(id, {
        limit: 1000,
        offset: 0,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new RelatedQuestions(),
    placeholderData: keepPreviousData,
    enabled: Boolean(id),
  });

  const reviewDetail = useQuery({
    queryKey: [QUERY_KEY_COMPANY_REVIEW_DETAIL, id],
    queryFn: () => CompanyService.getCompanyReviewDetail(id ?? ''),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new CompanyReviewDetail(),
    enabled: Boolean(id),
  });
  const stock = useQuery({
    queryKey: [QUERY_KEY_COMPANY_MARKET_PLACE, id],
    queryFn: () => CompanyService.getCompanyMarketplace(id, { limit: 1000, offset: 0 }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialData: new CompanyMarketPlaces(),
    enabled: Boolean(id),
  });
  const webDetail = useQueryCompanyWebDetail(id ?? '');

  const isFetching = some([
    overview.isFetching,
    address.isFetching,
    socialMedia.isFetching,
    review.isFetching,
    contact.isFetching,
    socialReview.isFetching,
    reviewRelatedQuestion.isFetching,
    reviewDetail.isFetching,
    stock.isFetching,
    webDetail.isFetching,
  ]);

  return {
    overview,
    address,
    isFetching,
    socialMedia: {
      ...socialMedia,
      data: { ...socialMedia.data, data: standardizeData.formatDataSocialCompany(socialMedia.data?.data) },
    },
    review,
    socialReview,
    reviewRelatedQuestion,
    reviewDetail,
    overviewFallback,
    webDetail,
    contact,
    stock,
  };
};
